import { useState } from 'react';

import { getContext } from '@/context';
import isAuthorized from '@/utils/user/isAuthorized';
import useMount from './useMount';

export type AuthStatus = 'pending' | 'authorized' | 'unauthorized';

// Host auth has no explicit "loading" flag: a signed-out user and a not-yet-
// loaded profile both look the same (no id / no token). Give auth a window to
// arrive before settling to unauthorized. The host holds bootstrap until auth
// settles, so this window only ever covers the profile-fetch tail.
const AUTH_SETTLE_MS = 1500;

/**
 * Reports the settled auth status, derived from the real host signals
 * (`general.auth.id` or the Authorization token — see isAuthorized).
 *
 * Resolves `authorized` as soon as either signal appears; otherwise settles to
 * `unauthorized` once the window elapses. Stays `pending` until then so
 * auth-gated UI doesn't flash the signed-out state during boot.
 */
const useReadAuthState = (): AuthStatus => {
  const [status, setStatus] = useState<AuthStatus>(() =>
    isAuthorized() ? 'authorized' : 'pending'
  );

  useMount(() => {
    if (isAuthorized()) return;

    try {
      const model = getContext().general.auth;

      const timer = setTimeout(() => {
        setStatus(isAuthorized() ? 'authorized' : 'unauthorized');
      }, AUTH_SETTLE_MS);

      const unsubscribe = model.subscribe(
        () => {
          if (isAuthorized()) {
            clearTimeout(timer);
            setStatus('authorized');
          }
        },
        { needPrevious: true }
      );

      return () => {
        clearTimeout(timer);
        unsubscribe?.();
      };
    } catch {
      setStatus('unauthorized');
    }
  });

  return status;
};

export default useReadAuthState;
